import { defineStore } from 'pinia';
import {
  apiFetchData, clearSession, getToken, getUser, registerUnauthorizedHandler, setSession,
} from '@/lib/api';
import { isValidGoogleIdToken } from '@/lib/google';
import { appMessage } from '@/lib/message';
import { AuthLoginDataSchema, AuthUserSchema, type AuthLoginResponse, type UserInfo } from '@/lib/schemas';
import { ALL_ACCESS, PERMISSIONS } from '@/constants/permissions';
import router from '@/router';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: getToken() as string | null,
    user: getUser<UserInfo>() as UserInfo | null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,

    permissions: (state): string[] => state.user?.permissions ?? [],

    /** `*` is all-access, matching has_permission() in src/auth.py. */
    hasPermission: (state) => (perm: string): boolean => {
      const held = state.user?.permissions ?? [];
      return held.includes(ALL_ACCESS) || held.includes(perm);
    },
  },

  actions: {
    async loginWithGoogle(idToken: string) {
      if (!isValidGoogleIdToken(idToken)) {
        throw new Error('Google returned a malformed credential. Try signing in again.');
      }

      const data = await apiFetchData<AuthLoginResponse>(
        '/api/auth/google',
        { method: 'POST', body: JSON.stringify({ idToken }) },
        AuthLoginDataSchema,
      );

      const held = data.user.permissions ?? [];
      if (!held.includes(ALL_ACCESS) && !held.includes(PERMISSIONS.ACCESS)) {
        // The server refuses this too; checking here keeps the message
        // specific and, importantly, stores NO session.
        appMessage.warning(
          `${data.user.email} has no access to the proxy dashboard. `
          + 'Ask the developer team to grant it.',
        );
        await router.replace('/login').catch(() => undefined);
        return;
      }

      setSession(data.token, data.user);
      this.token = data.token;
      this.user = data.user;
    },

    /**
     * Re-read the user from the server. Permissions ride in the JWT, so this is
     * how a permission change takes effect without waiting out the token.
     */
    async fetchUserInfo() {
      if (!this.token) return;
      const info = await apiFetchData<UserInfo>('/api/auth/me', {}, AuthUserSchema);
      this.user = info;
      setSession(this.token, info);
    },

    logout() {
      clearSession();
      this.token = null;
      this.user = null;
    },
  },
});

/**
 * A 401 from any request logs out, which main.ts turns into a redirect.
 * Registered at module scope so lib/api.ts never has to import this store.
 */
registerUnauthorizedHandler(() => {
  useAuthStore().logout();
});
